/**
 * A small horizontal bar chart for "at a glance" dashboard figures — one
 * series, ordered categories (an aging bucket, an urgency window), bar length
 * the magnitude. Hand-rolled divs rather than SVG: a percentage-width bar is
 * simpler to reason about than viewBox math, and it reflows with the
 * dashboard grid instead of needing its own resize handling.
 *
 * Every bar is directly labeled with its bucket name and value as real text
 * (never color-only), so a single series needs no legend — the widget's own
 * title already says what it is. `severity` steps a single brand hue from
 * light to dark across the ordered buckets via `color-mix`, rather than a
 * multi-hue categorical palette: these bars are one measurement at increasing
 * severity, not four different things.
 */

export interface MiniBarDatum {
  label: string;
  value: number;
  displayValue: string;
}

export function MiniBarChart({
  data,
  colorVar,
  severity = 'ascending',
}: {
  data: MiniBarDatum[];
  /** A CSS custom property name, e.g. '--down'. */
  colorVar: string;
  /** 'ascending': last bucket is most severe (AR aging). 'descending': first
   *  bucket is most urgent (lease expirations — soonest is worst). */
  severity?: 'ascending' | 'descending';
}) {
  const max = Math.max(1, ...data.map((d) => d.value));

  return (
    <div className="mini-bar-chart">
      {data.map((d, i) => {
        const step = data.length <= 1 ? 1 : i / (data.length - 1);
        const intensity = severity === 'ascending' ? step : 1 - step;
        const mixPct = Math.round(40 + intensity * 60);
        return (
          <div className="mini-bar-chart__row" key={d.label}>
            <span className="mini-bar-chart__label">{d.label}</span>
            <span className="mini-bar-chart__track" title={`${d.label}: ${d.displayValue}`}>
              <span
                className="mini-bar-chart__fill"
                style={{
                  width: `${(d.value / max) * 100}%`,
                  background: `color-mix(in srgb, var(${colorVar}) ${mixPct}%, var(--surface))`,
                }}
              />
            </span>
            <span className="mini-bar-chart__value">{d.displayValue}</span>
          </div>
        );
      })}
    </div>
  );
}
